/**
 * Endgame Solver
 *
 * Provides perfect play in endgame positions using:
 * - Exact minimax search when few squares remain
 * - Efficient move generation and evaluation
 * - Parity analysis for optimal timing
 * - Transposition tables for performance
 */

import { PlayerColor, BoardPosition } from '@/types/game-types';
import type { SimulatedGameState } from '../game-simulator';
import { GameSimulator } from '../game-simulator';

/**
 * Endgame search result
 */
export interface EndgameResult {
  readonly bestMove: BoardPosition;
  readonly exactScore: number;      // Exact final score difference
  readonly movesToEnd: number;      // Number of moves until game end
  readonly isWin: boolean;          // True if position is winning
  readonly nodesSearched: number;   // Performance metric
  readonly timeElapsed: number;     // Time taken for calculation
  readonly confidence: number;      // Always 1.0 for exact solutions
}

/**
 * Endgame solver configuration
 */
export interface EndgameSolverConfig {
  maxEmptySquares: number;          // Solve when <= this many empty squares
  useTranspositionTable: boolean;   // Cache results
  useMoveOrdering: boolean;         // Order moves for better pruning
  maxTimeMs: number;               // Maximum solving time
  enableParity: boolean;           // Use parity analysis
}

/**
 * Endgame transposition table entry
 */
interface EndgameTransposition {
  exactScore: number;
  depth: number;
  bestMove?: BoardPosition;
  flag: 'exact' | 'lower' | 'upper';
  timestamp: number;
}

/**
 * Endgame Solver Implementation
 */
export class EndgameSolver {
  private readonly _config: EndgameSolverConfig;
  private readonly _transpositionTable = new Map<string, EndgameTransposition>();
  private _nodesSearched = 0;
  private _startTime = 0;
  private _timeLimit = 0;

  constructor(config: Partial<EndgameSolverConfig> = {}) {
    this._config = {
      maxEmptySquares: 16,
      useTranspositionTable: true,
      useMoveOrdering: true,
      maxTimeMs: 30000, // 30 seconds max
      enableParity: true,
      ...config,
    };
  }

  /**
   * Solve endgame position exactly
   */
  async solvePosition(
    gameState: SimulatedGameState,
    player: PlayerColor,
    timeLimit?: number,
  ): Promise<EndgameResult> {
    this._startTime = Date.now();
    this._timeLimit = timeLimit || this._config.maxTimeMs;
    this._nodesSearched = 0;

    // Verify this is an endgame position
    const emptySquares = this.countEmptySquares(gameState.board);
    if (emptySquares > this._config.maxEmptySquares) {
      throw new Error(`Position has ${emptySquares} empty squares, exceeds maximum of ${this._config.maxEmptySquares}`);
    }

    const result = await this.exactSearch(gameState, player, emptySquares, -64, 64);

    const timeElapsed = Date.now() - this._startTime;

    if (!result.bestMove && result.bestMove !== 0) {
      throw new Error('No valid move found in endgame position');
    }

    return {
      bestMove: result.bestMove,
      exactScore: result.score,
      movesToEnd: emptySquares,
      isWin: result.score > 0,
      nodesSearched: this._nodesSearched,
      timeElapsed,
      confidence: 1.0, // Exact solution
    };
  }

  /**
   * Check if position qualifies for endgame solving
   */
  canSolve(gameState: SimulatedGameState): boolean {
    const emptySquares = this.countEmptySquares(gameState.board);
    return emptySquares <= this._config.maxEmptySquares;
  }

  /**
   * Exact minimax search for endgame
   */
  private async exactSearch(
    gameState: SimulatedGameState,
    originalPlayer: PlayerColor,
    depth: number,
    alpha: number,
    beta: number,
  ): Promise<{ score: number; bestMove?: BoardPosition }> {
    this._nodesSearched++;

    // Check time limit
    if (Date.now() - this._startTime >= this._timeLimit) {
      throw new Error('Endgame solver timeout');
    }

    // Check transposition table
    if (this._config.useTranspositionTable) {
      const ttResult = this.lookupTransposition(gameState, depth);
      if (ttResult) {
        return { score: ttResult.exactScore, bestMove: ttResult.bestMove };
      }
    }

    // Terminal position - calculate exact final score
    if (gameState.gameOver || gameState.validMoves.size === 0) {
      const finalScore = this.calculateFinalScore(gameState, originalPlayer);
      return { score: finalScore };
    }

    // Get ordered moves
    const moves = this.getOrderedMoves(gameState);
    let bestMove: BoardPosition | undefined;
    let bestScore = gameState.currentPlayer === originalPlayer ? -64 : 64;

    for (const move of moves) {
      const newState = GameSimulator.makeMove(gameState, move);
      if (!newState) continue;

      // Handle pass if no valid moves for next player
      let nextState = newState;
      if (nextState.validMoves.size === 0 && !nextState.gameOver) {
        nextState = GameSimulator.makePass(nextState);
      }

      const result = await this.exactSearch(
        nextState,
        originalPlayer,
        depth - 1,
        alpha,
        beta,
      );

      // Update best score and alpha-beta bounds
      if (gameState.currentPlayer === originalPlayer) {
        // Maximizing player
        if (result.score > bestScore) {
          bestScore = result.score;
          bestMove = move;
        }
        alpha = Math.max(alpha, bestScore);
      } else {
        // Minimizing player
        if (result.score < bestScore) {
          bestScore = result.score;
          bestMove = move;
        }
        beta = Math.min(beta, bestScore);
      }

      // Alpha-beta pruning
      if (beta <= alpha) {
        break;
      }
    }

    // Store in transposition table
    if (this._config.useTranspositionTable) {
      this.storeTransposition(gameState, depth, bestScore, bestMove, alpha, beta);
    }

    return { score: bestScore, bestMove };
  }

  /**
   * Calculate exact final score (difference in piece count)
   */
  private calculateFinalScore(gameState: SimulatedGameState, originalPlayer: PlayerColor): number {
    let playerPieces = 0;
    let opponentPieces = 0;
    let emptySquares = 0;

    for (let i = 0; i < gameState.board.length; i++) {
      const piece = gameState.board[i];
      if (piece === originalPlayer) {
        playerPieces++;
      } else if (piece !== PlayerColor.EMPTY) {
        opponentPieces++;
      } else {
        emptySquares++;
      }
    }

    // In endgame, empty squares go to the player who made the last move
    if (emptySquares > 0) {
      // Use parity to determine who gets the empty squares
      if (this._config.enableParity) {
        const parityAdvantage = this.calculateParity(gameState, originalPlayer);
        if (parityAdvantage > 0) {
          playerPieces += emptySquares;
        } else {
          opponentPieces += emptySquares;
        }
      } else {
        // Simple heuristic: current player gets remaining squares
        if (gameState.currentPlayer === originalPlayer) {
          playerPieces += emptySquares;
        } else {
          opponentPieces += emptySquares;
        }
      }
    }

    return playerPieces - opponentPieces;
  }

  /**
   * Calculate parity advantage (who is likely to get the last move)
   */
  private calculateParity(gameState: SimulatedGameState, player: PlayerColor): number {
    const emptySquares = this.countEmptySquares(gameState.board);
    const currentPlayerAdvantage = gameState.currentPlayer === player ? 1 : -1;

    // In positions with odd number of empty squares, current player has advantage
    if (emptySquares % 2 === 1) {
      return currentPlayerAdvantage;
    } else {
      return -currentPlayerAdvantage;
    }
  }

  /**
   * Get ordered moves for better alpha-beta pruning
   */
  private getOrderedMoves(gameState: SimulatedGameState): BoardPosition[] {
    const moves = Array.from(gameState.validMoves.keys());

    if (!this._config.useMoveOrdering) {
      return moves;
    }

    // Order moves by strategic value in endgame
    const scoredMoves = moves.map(move => {
      let score = 0;

      // Prioritize corner moves
      if ([0, 7, 56, 63].includes(move)) {
        score += 1000;
      }

      // Prioritize edge moves
      const row = Math.floor(move / 8);
      const col = move % 8;
      if (row === 0 || row === 7 || col === 0 || col === 7) {
        score += 100;
      }

      // Prioritize moves that flip more pieces
      const flippedCount = gameState.validMoves.get(move)?.length || 0;
      score += flippedCount * 10;

      // Avoid X-squares unless necessary
      if ([9, 14, 49, 54].includes(move)) {
        score -= 500;
      }

      return { move, score };
    });

    // Sort by score descending
    scoredMoves.sort((a, b) => b.score - a.score);

    return scoredMoves.map(item => item.move);
  }

  /**
   * Count empty squares on board
   */
  private countEmptySquares(board: Uint8Array): number {
    let count = 0;
    for (let i = 0; i < board.length; i++) {
      if (board[i] === PlayerColor.EMPTY) {
        count++;
      }
    }
    return count;
  }

  /**
   * Generate transposition table key
   */
  private generateTranspositionKey(gameState: SimulatedGameState): string {
    return Array.from(gameState.board).join('') + '_' + gameState.currentPlayer;
  }

  /**
   * Lookup transposition table
   */
  private lookupTransposition(
    gameState: SimulatedGameState,
    depth: number,
  ): EndgameTransposition | null {
    const key = this.generateTranspositionKey(gameState);
    const entry = this._transpositionTable.get(key);

    if (entry && entry.depth >= depth) {
      return entry;
    }

    return null;
  }

  /**
   * Store result in transposition table
   */
  private storeTransposition(
    gameState: SimulatedGameState,
    depth: number,
    score: number,
    bestMove: BoardPosition | undefined,
    alpha: number,
    beta: number,
  ): void {
    const key = this.generateTranspositionKey(gameState);

    let flag: 'exact' | 'lower' | 'upper';
    if (score <= alpha) {
      flag = 'upper';
    } else if (score >= beta) {
      flag = 'lower';
    } else {
      flag = 'exact';
    }

    this._transpositionTable.set(key, {
      exactScore: score,
      depth,
      bestMove,
      flag,
      timestamp: Date.now(),
    });

    // Limit table size
    if (this._transpositionTable.size > 50000) {
      this.cleanupTranspositionTable();
    }
  }

  /**
   * Clean up old transposition table entries
   */
  private cleanupTranspositionTable(): void {
    const cutoff = Date.now() - 600000; // 10 minutes
    for (const [key, entry] of this._transpositionTable.entries()) {
      if (entry.timestamp < cutoff) {
        this._transpositionTable.delete(key);
      }
    }
  }

  /**
   * Get solver statistics
   */
  getStatistics(): {
    transpositionEntries: number;
    maxEmptySquares: number;
    averageNodesPerSecond: number;
  } {
    const timeElapsed = Date.now() - this._startTime;
    const nodesPerSecond = timeElapsed > 0 ? (this._nodesSearched / timeElapsed) * 1000 : 0;

    return {
      transpositionEntries: this._transpositionTable.size,
      maxEmptySquares: this._config.maxEmptySquares,
      averageNodesPerSecond: nodesPerSecond,
    };
  }

  /**
   * Clear all caches
   */
  clearCaches(): void {
    this._transpositionTable.clear();
  }

  /**
   * Update configuration
   */
  setConfig(config: Partial<EndgameSolverConfig>): void {
    Object.assign(this._config, config);
  }

  /**
   * Get current configuration
   */
  getConfig(): EndgameSolverConfig {
    return { ...this._config };
  }
}

/**
 * Default endgame solver instance
 */
export const defaultEndgameSolver = new EndgameSolver();

/**
 * Factory functions for creating endgame solvers
 */
export const createEndgameSolver = {
  /**
   * Fast solver for quick endgames
   */
  fast(): EndgameSolver {
    return new EndgameSolver({
      maxEmptySquares: 12,
      maxTimeMs: 5000,
      useMoveOrdering: true,
    });
  },

  /**
   * Deep solver for thorough analysis
   */
  deep(): EndgameSolver {
    return new EndgameSolver({
      maxEmptySquares: 20,
      maxTimeMs: 60000,
      useTranspositionTable: true,
      useMoveOrdering: true,
      enableParity: true,
    });
  },

  /**
   * Tournament solver for competitive play
   */
  tournament(): EndgameSolver {
    return new EndgameSolver({
      maxEmptySquares: 16,
      maxTimeMs: 30000,
      useTranspositionTable: true,
      useMoveOrdering: true,
      enableParity: true,
    });
  },

  /**
   * Custom configuration
   */
  custom(config: Partial<EndgameSolverConfig>): EndgameSolver {
    return new EndgameSolver(config);
  },
};